import { isHidden, valueOrNull } from "@/shared/value";
import { CompanyDocumentTreeNode } from "../model/types";
import { isFolderNode } from "./tree-node-guards";
import { ancestorsOf } from "./ancestors-of";

/** Идентификатор звена «корень дерева» на панели уровней. */
export const ROOT_DROP_ID = "company-document-tree-root";

/** Идентификатор самой панели уровней: её фон закрывает строки дерева под ней. */
export const LEVEL_PANEL_DROP_ID = "company-document-tree-level-panel";

const ANCESTOR_DROP_PREFIX = "company-document-tree-ancestor:";

/**
 * Идентификатор звена папки на панели уровней. Свой, а не id узла: строка этой же
 * папки в дереве тоже приёмник, а dnd-kit требует уникальных идентификаторов.
 */
export function ancestorDropId(nodeId: string): string {
  return ANCESTOR_DROP_PREFIX + nodeId;
}

/** Звено панели уровней: корень или папка из пути узла. */
export function isLevelLinkDropId(id: string): boolean {
  return id === ROOT_DROP_ID || id.startsWith(ANCESTOR_DROP_PREFIX);
}

export type DropTarget = {
  /** Что подсветить: узел папки-приёмника или звено корня. */
  key: string;
  /** Новая папка узла; `null` — корень дерева. */
  folderId: string | null;
};

// `undefined` — папка скрыта защитой: её идентификатора у клиента нет.
function folderIdOf(node: CompanyDocumentTreeNode): string | undefined {
  if (!isFolderNode(node) || isHidden(node.folder)) {
    return undefined;
  }

  return valueOrNull(node.folder)?.id;
}

function folderNodeOf(
  node: CompanyDocumentTreeNode,
  nodeById: Map<string, CompanyDocumentTreeNode>,
): CompanyDocumentTreeNode | undefined {
  if (isFolderNode(node)) {
    return node;
  }

  const parent = node.parentId ? nodeById.get(node.parentId) : undefined;

  return parent && isFolderNode(parent) ? parent : undefined;
}

function isSelfOrDescendant(
  node: CompanyDocumentTreeNode,
  ancestorId: string,
  nodeById: Map<string, CompanyDocumentTreeNode>,
): boolean {
  return (
    node.id === ancestorId ||
    ancestorsOf(node, nodeById).some((ancestor) => ancestor.id === ancestorId)
  );
}

/**
 * Куда ляжет перетаскиваемый узел, если отпустить его над `overId`: над папкой — в неё,
 * над файлом — в папку этого файла, над звеном панели уровней — в эту папку или
 * в корень. Возвращает `undefined`, когда бросок ничего не изменит или невозможен:
 * папка-приёмник скрыта, папку тянут в саму себя или в своего потомка, узел уже
 * лежит в этой папке.
 */
export function resolveDropTarget(
  dragged: CompanyDocumentTreeNode,
  overId: string,
  nodeById: Map<string, CompanyDocumentTreeNode>,
): DropTarget | undefined {
  const draggedParent = dragged.parentId
    ? nodeById.get(dragged.parentId)
    : undefined;

  if (overId === ROOT_DROP_ID) {
    return draggedParent ? { key: ROOT_DROP_ID, folderId: null } : undefined;
  }

  const over = nodeById.get(
    overId.startsWith(ANCESTOR_DROP_PREFIX)
      ? overId.slice(ANCESTOR_DROP_PREFIX.length)
      : overId,
  );
  const targetFolder = over ? folderNodeOf(over, nodeById) : undefined;

  // Файл в корне: его папка — сам корень.
  if (over && !targetFolder) {
    return resolveDropTarget(dragged, ROOT_DROP_ID, nodeById);
  }

  if (!targetFolder || targetFolder.id === draggedParent?.id) {
    return undefined;
  }

  if (
    isFolderNode(dragged) &&
    isSelfOrDescendant(targetFolder, dragged.id, nodeById)
  ) {
    return undefined;
  }

  const folderId = folderIdOf(targetFolder);

  return folderId ? { key: targetFolder.id, folderId } : undefined;
}
